import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { FiEdit, FiTrash, FiPlus } from 'react-icons/fi';
import { feeCategoriesService } from '../../services/api';
import ConditionalButton from '../../components/common/ConditionalButton';
import { usePermissions } from '../../hooks/usePermissions';

const FeeCategoriesPage = () => {
  const { canUpdate, canDelete } = usePermissions();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    name: '', code: '', priority: 50, is_mandatory: 1, is_active: 1,
  });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCategories = async () => {
    const res = await feeCategoriesService.getAll();
    setCategories(res.data.data || []);
  };

  useEffect(() => { fetchCategories(); }, []);

  const resetForm = () => {
    setForm({ name: '', code: '', priority: 50, is_mandatory: 1, is_active: 1 });
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingId) {
        await feeCategoriesService.update(editingId, form);
        Swal.fire({ icon: 'success', title: 'Rubrique mise à jour', timer: 2000, showConfirmButton: false });
      } else {
        await feeCategoriesService.create(form);
        Swal.fire({ icon: 'success', title: 'Rubrique créée', timer: 2000, showConfirmButton: false });
      }
      resetForm();
      fetchCategories();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: err.response?.data?.message || 'Erreur inattendue' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (cat) => {
    setEditingId(cat.id);
    setForm({
      name: cat.name,
      code: cat.code,
      priority: cat.priority,
      is_mandatory: cat.is_mandatory,
      is_active: cat.is_active,
    });
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer cette rubrique ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Supprimer',
      cancelButtonText: 'Annuler',
    });
    if (!result.isConfirmed) return;
    try {
      await feeCategoriesService.delete(id);
      fetchCategories();
      Swal.fire({ icon: 'success', title: 'Rubrique supprimée', timer: 2000, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: err.response?.data?.message });
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">Rubriques de frais</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium mb-4">{editingId ? 'Modifier' : 'Nouvelle rubrique'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nom</label>
              <input type="text" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2" placeholder="Ex: Scolarité" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Code</label>
              <input type="text" required value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2" placeholder="Ex: SCOLARITE" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Priorité (plus élevé = plus prioritaire)</label>
              <input type="number" value={form.priority}
                onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2" />
            </div>
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input type="checkbox" checked={form.is_mandatory === 1}
                  onChange={(e) => setForm({ ...form, is_mandatory: e.target.checked ? 1 : 0 })}
                  className="mr-2" />
                Obligatoire
              </label>
              <label className="flex items-center">
                <input type="checkbox" checked={form.is_active === 1}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })}
                  className="mr-2" />
                Active
              </label>
            </div>
            <div className="flex space-x-2">
              <ConditionalButton entity="frais-scolarite" permission="create" type="submit"
                disabled={loading}
                className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50">
                {editingId ? 'Mettre à jour' : 'Créer'}
              </ConditionalButton>
              {editingId && (
                <button type="button" onClick={resetForm}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300">
                  Annuler
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="lg:col-span-2 bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priorité</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {categories.map((cat) => (
                <tr key={cat.id}>
                  <td className="px-4 py-3 text-sm font-medium">{cat.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{cat.code}</td>
                  <td className="px-4 py-3 text-sm">{cat.priority}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 text-xs rounded-full ${cat.is_mandatory ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
                      {cat.is_mandatory ? 'Obligatoire' : 'Optionnel'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canUpdate('frais-scolarite') && (
                      <button onClick={() => handleEdit(cat)} className="text-primary-600 hover:text-primary-900 mr-3">
                        <FiEdit />
                      </button>
                    )}
                    {canDelete('frais-scolarite') && (
                      <button onClick={() => handleDelete(cat.id)} className="text-red-600 hover:text-red-900">
                        <FiTrash />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {categories.length === 0 && (
                <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-500">Aucune rubrique configurée</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FeeCategoriesPage;
